/**
 * ProofsSection — mục "Bằng chứng" trong chi tiết đơn.
 *
 * Ảnh giao khách đứng riêng; ảnh trước/sau gom theo từng người trang điểm (một
 * đơn có thể nhiều makeup, mỗi người một bộ), và trong mỗi người tách HAI hàng
 * TRƯỚC / SAU (CEO chốt 30/09/2026 — trước đây trộn chung một dải nên không
 * phân biệt được). Mọi tấm đều hiện, kèm giờ chụp để quản lý đối chiếu.
 *
 * Nút ✕ trên tấm nào do máy chủ quyết (`removable`): người chụp khi còn hạn
 * nộp, quản trị/giám đốc mọi lúc.
 */
import { PhotoThumbList } from "@/components/PhotoThumb";
import { useOrderProofs } from "./api";
import { ProofPhotoRow } from "./ProofPhotoRow";
import { KIND_LABEL, photosOf, proofsOfKind, type OrderProof } from "./utils";

function fmtTime(iso: string): string {
  return new Date(iso).toLocaleString("vi-VN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Ảnh giao khách — khung 8x10 xem lớn tại chỗ (CEO chốt 27/09/2026). */
function DeliveryGrid({ items }: { items: OrderProof[] }) {
  const anh = photosOf(items);
  return (
    <PhotoThumbList
      images={anh.map((a) => a.src)}
      alt={(i) => KIND_LABEL[anh[i]!.proof.kind]}
      openLabel={(i) => `Xem lớn ${KIND_LABEL[anh[i]!.proof.kind].toLowerCase()} ${i + 1}`}
      caption={(_, i) => (
        <div className="mt-0.5 max-w-[240px] truncate text-xs text-muted-foreground">
          {KIND_LABEL[anh[i]!.proof.kind]} · {fmtTime(anh[i]!.proof.createdAt)}
        </div>
      )}
    />
  );
}

export function ProofsSection({ orderId }: { orderId: string }) {
  const { data: proofs = [] } = useOrderProofs(orderId);
  if (proofs.length === 0) return null;

  const delivery = proofsOfKind(proofs, "delivery");
  const byMakeup = new Map<string, OrderProof[]>();
  for (const p of proofs) {
    if (p.kind === "delivery") continue;
    byMakeup.set(p.takenBy, [...(byMakeup.get(p.takenBy) ?? []), p]);
  }

  return (
    <div className="space-y-3 rounded-md border bg-surface p-3">
      <p className="text-xs font-medium text-muted-foreground">Bằng chứng</p>

      {delivery.length > 0 ? (
        <div>
          <p className="mb-1 text-[11px] font-semibold">
            Giao ảnh — {delivery[0]?.takenByName}
            {delivery[0]?.note ? ` · ${delivery[0].note}` : ""}
          </p>
          <DeliveryGrid items={delivery} />
        </div>
      ) : null}

      {[...byMakeup.entries()].map(([staffId, items]) => (
        <div key={staffId} className="space-y-2">
          <p className="text-[11px] font-semibold">
            Trước / sau trang điểm — {items[0]?.takenByName}
          </p>
          <ProofPhotoRow
            orderId={orderId}
            label="Trước trang điểm"
            photos={photosOf(proofsOfKind(items, "makeup_before"))}
            emptyText="Chưa có ảnh trước trang điểm"
          />
          <ProofPhotoRow
            orderId={orderId}
            label="Sau trang điểm"
            photos={photosOf(proofsOfKind(items, "makeup_after"))}
            emptyText="Chưa có ảnh sau trang điểm"
          />
        </div>
      ))}
    </div>
  );
}
